<script lang="ts">
	// Svelte hooks
	import { onDestroy, onMount } from 'svelte';
	import { get } from 'svelte/store';
	// Services
	import { format } from '../../services/i18n';
	// Stores
	import { home_data, user_data, user_settings } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';
	import { eventLogStore } from 'src/stores/features/event-log';
	// Helpers
	import { capFirstChar, sortOn } from '../../helpers/utilities';
	import { getEvents, getTagsAndDeviceChannel, parseEventLogData } from './event_log_helper';
	import { save_home } from '../../helpers/home_helpers';
	import { defaultSelectedRoom } from '../../helpers/constants';
	import { isCameraDevice, real_id } from 'src/helpers/interface_helpers';
	import {
		accountHasCameraCloudStorage,
		hasCameraCloudStorage,
		hydrateFromUserSettings,
	} from 'src/helpers/camera_cloud_storage_helpers';
	import { hasCameraDevices } from 'src/components/Camera/helpers/camera-helpers';
	import { eventLogV2Enabled } from 'src/stores/event_log_v2_enabled';
	import { setSidePanelState } from '../Sidepanel/sidepanel-helpers';
	// Types
	import type { EventsLogData } from '../../types/requests/eventslog/events_log';
	import type { Device } from '../../types/Device';
	// Design system components
	import { Alert, Button, Loader, CircleAvatar, Text } from '@Shelly/shelly-design-system';
	import PremiumUpsellAlert from 'src/components/Premium/PremiumUpsellAlert.svelte';
	// Components
	import EventTableToolbar from './EventTableToolbar.svelte';
	import EventTable from './EventTable.svelte';
	import EventTableV2 from './EventTableV2.svelte';
	import EmptyList from '../Lists/EmptyList.svelte';
	import EmptyDeviceList from '../Lists/EmptyDeviceList.svelte';
	import NoSearchResults from '../Lists/NoSearchResults.svelte';
	import ListEmptyStateSurface from '../Lists/ListEmptyStateSurface.svelte';

	export let device: Device = null;
	export let devicesArr: Device[] | [] = [];
	export let type: 'global' | 'device' | 'group' = 'global';
	export let openedEventTimestamp = null;

	const nonPremiumEventsLimit = 5;
	const premiumEventsLimit = 100;

	// Showing 100 events per step
	const numberShowEvents = 100;

	$: hasCamera = type === 'device' ? isCameraDevice(device) : hasCameraDevices($devices);

	$: hasCloudStorageEvents = hasCamera && checkCloudStorage($user_settings);

	$: hasExtendedEventLimit = $user_data?.premium || hasCloudStorageEvents;

	$: limit = hasExtendedEventLimit ? premiumEventsLimit : nonPremiumEventsLimit;

	let deviceChannel = -1,
		waitForResponse = false,
		sortByDesc = true,
		sortByTitle = 'timestamp',
		filterByType = 'all',
		filterByRoomID = defaultSelectedRoom.value,
		searchByDeviceName = '',
		tags = [],
		result: EventsLogData[] = null,
		pointer: string = null,
		events = [],
		hasErrors,
		haSQLErr;

	let visibleEntriesCount = 0; // Index of how much event we need to show, used with the slice
	let eventsToShow = []; // Array with the events which we show
	let showRetry = false; // If we have to show retry button due to failed request

	$: allDevices = Object.values($devices);
	$: onlySharedDevices = allDevices.every((d: Device) => d.shared);
	$: isEmptyDeviceList = allDevices.length === 0 || onlySharedDevices;

	onMount(async () => {
		const [newTags, newDevicesChannel] = getTagsAndDeviceChannel(device, devicesArr);
		tags = [...newTags];
		deviceChannel = newDevicesChannel;

		await getEventLogStatistic();
		if (device) {
			eventLogStore.deviceId = device.id;
			eventLogStore.channel = deviceChannel.toString();
		}
	});

	onDestroy(() => {
		eventLogStore.clear();
		unsubscribe();
	});

	/**
	 * Hydrate the Cloud Storage slot and check if the device allows Cloud Storage events.
	 */
	const checkCloudStorage = (settings: any): boolean => {
		hydrateFromUserSettings(settings);
		return type === 'device' ? hasCameraCloudStorage(device?.id) : accountHasCameraCloudStorage();
	};

	const getEventLogStatistic = async (loadMore: boolean = false) => {
		waitForResponse = true;
		showRetry = false;

		const res = await getEvents(loadMore, pointer, tags, limit);
		//@ts-ignore
		hasErrors = res?.errors?.length > 0;
		haSQLErr = hasErrors
			? Array.isArray(res?.errors) && res.errors.filter((e) => e.err?.code === 'SQLITE_ERROR')
			: undefined;

		if (haSQLErr) {
			console.error('%c SQLITE_ERRORS', 'color: orange', res?.errors);
		}

		if (type === 'global' && res?.hasOwnProperty('isok') && !res.isok) {
			showRetry = true;
		}

		if (res?.result || hasErrors) {
			//including errors only also to trigger parseEventLogData
			const reqResult: EventsLogData[] = parseEventLogData(res, deviceChannel);
			result = loadMore && pointer ? [...result, ...reqResult] : reqResult;
			pointer = res?.pointer;
			filterEvents();
			loadMoreEntries();
		}

		waitForResponse = false;
	};

	/**
	 * `filterByType` mixes event categories ('sensors' -> 'sensor') with the
	 * 'lights' quick filter, which is a device trait rather than a category.
	 */
	const matchesTypeFilter = (event: EventsLogData): boolean => {
		if (filterByType === 'all') return true;
		if (filterByType === 'lights') return !!event.isLight;
		return event.category === filterByType.slice(0, -1);
	};

	const filterEvents = () => {
		// Uncomment to prevent table sorting in device drawer
		// if (device !== null) {
		// 	events = result;
		// 	return;
		// }

		// Zero the slice index to start from the beginning with the new filtered
		visibleEntriesCount = 0;

		events = sortOn(
			result?.filter(
				(i) =>
					matchesTypeFilter(i) &&
					i.roomID ==
						(filterByRoomID !== defaultSelectedRoom.value ? filterByRoomID : i.roomID) &&
					i.name.toLowerCase().includes(searchByDeviceName),
			),
			sortByTitle,
			sortByDesc,
		);

		loadMoreEntries();
	};

	/**
	 * Function to append more event to be shown
	 */
	const loadMoreEntries = () => {
		visibleEntriesCount += numberShowEvents;
		eventsToShow = events?.slice(0, visibleEntriesCount) || [];
	};

	const filterBy = (type: string) => {
		filterByType = type;
		filterEvents();
	};

	const filterByRoom = (roomID: string) => {
		filterByRoomID = roomID;
		filterEvents();
	};

	const searchByName = (name: string) => {
		searchByDeviceName = name.trim().toLowerCase();
		filterEvents();
	};

	const sortBy = (e: CustomEvent) => {
		sortByTitle = e.detail.title;
		sortByDesc = e.detail.desc;
		filterEvents();
	};

	const updateHomeData = () => {
		const dateNow = new Date();

		save_home('premiumEventLogAdvertise', {
			seen: true,
			date: dateNow,
		});

		home_data.set({
			...$home_data,
			premiumEventLogAdvertise: {
				seen: true,
				date: dateNow,
			},
		});
	};

	const unsubscribe = eventLogStore.getAllEvents(async (newEvents) => {
		// Directly parse newEvents without checking for newEvents.result
		let items = await parseEventLogData(newEvents, deviceChannel);
		const validResult = (result || []).filter((e) => e.timestamp !== undefined);
		eventsToShow = sortOn([...items, ...validResult], 'timestamp', true).slice(
			0,
			visibleEntriesCount,
		);
	});
</script>

{#if type !== 'device'}
	<EventTableToolbar
		{type}
		{isEmptyDeviceList}
		on:filterBy={(e) => filterBy(e.detail.value)}
		on:filterByRoom={(e) => filterByRoom(e.detail.value)}
		on:searchByName={(e) => searchByName(e.detail.value)}
		on:refresh={() => getEventLogStatistic()}
	/>
{/if}

{#if waitForResponse && pointer == null}
	<div class="flex-full-height align-items-center justify-content-center">
		<Loader class="d-flex justify-content-center" />
	</div>
{:else if isEmptyDeviceList}
	<div class="flex-full-height justify-content-center align-items-center">
		<ListEmptyStateSurface class="mt-md-l mt-sm-s">
			<EmptyDeviceList {onlySharedDevices} />
		</ListEmptyStateSurface>
	</div>
{:else if showRetry}
	<div class="flex-full-height justify-content-center align-items-center">
		<ListEmptyStateSurface class="mt-md-l mt-sm-s">
			<EmptyList
				icon="fal fa-notebook"
				title={$format('err_msg_fetch_activity_log', {
					default: 'There was an error fetching the activity log.',
				})}
				description={$format('err_msg_please_try_again', {
					default: 'Please try again!',
				})}
				btnText={$format('button_retry', { default: 'Retry' })}
				onClick={() => getEventLogStatistic()}
			/>
		</ListEmptyStateSurface>
	</div>
{:else if events.length === 0}
	{#if device}
		<div class="flex-full-height align-items-center justify-content-center">
			<ListEmptyStateSurface>
				<CircleAvatar class="mb-l" size={'xxl'} icon={'fa-thin fa-face-sleeping'} />
				<Text element="h2" class="mb-s text-bold text-center">
					{capFirstChar(
						$format('label_title_no_events', {
							default: 'No activity info found.',
						}),
					)}
				</Text>
				<Text class="text-color-muted text-center" styleType="body-small">
					{$format('label_description_no_events_device', {
						default: 'Make sure the device is powered and online to report activity.',
					})}
				</Text>
			</ListEmptyStateSurface>
		</div>
	{:else if searchByDeviceName}
		<ListEmptyStateSurface class="mt-md-l mt-sm-s">
			<NoSearchResults marginTop="0" marginTopMd="0" search={searchByDeviceName} />
		</ListEmptyStateSurface>
	{:else}
		<ListEmptyStateSurface class="mt-md-l mt-sm-s">
			<EmptyList
				icon="fal fa-notebook"
				title={$format('label_title_no_events', {
					default: 'No activity info found.',
				})}
				description={$format('label_description_no_events_global', {
					default:
						'Make sure you have added devices to your account and they are online to report activity.',
				})}
			/>
		</ListEmptyStateSurface>
	{/if}
{:else}
	<!-- TODO Update with real content for now is static -->
	{#if !hasExtendedEventLimit && !get(home_data).premiumEventLogAdvertise?.seen}
		<PremiumUpsellAlert
			class="mb-m"
			dismissable
			heading={$format('premium_event_log_alert_heading', {
				values: {
					nonPremiumEventsLimit,
					premiumEventsLimit,
				},
				default:
					'Free version allows 5 events per device. Upgrade to Premium and get up to 100 events per device.',
			})}
			onGetPremium={() => {
				setSidePanelState({ open: false });
			}}
			on:dismissable_click={updateHomeData}
		/>
	{/if}

	{#if $eventLogV2Enabled}
		<EventTableV2
			events={eventsToShow}
			{type}
			{openedEventTimestamp}
			devicePageLog={devicesArr.length === 1}
			on:sortBy={sortBy}
		/>
	{:else}
		<EventTable
			events={eventsToShow}
			{type}
			{openedEventTimestamp}
			devicePageLog={devicesArr.length === 1}
			deviceType={device ? device.type : ''}
			on:sortBy={sortBy}
		/>
	{/if}
{/if}

{#if hasErrors && !haSQLErr}
	<Alert
		class="mt-m"
		type="danger"
		heading={$format('activity_log_error_content', {
			default: 'Activity log contains errors from registering the devices events.',
		})}
	/>
{/if}

{#if !waitForResponse && visibleEntriesCount < events.length}
	<div class="text-center">
		<Button size="m" class="mb-m width-full width-md-l" on:click={() => loadMoreEntries()}>
			{$format('show_more_btn', { default: 'Show more' })}
		</Button>
	</div>
{/if}

{#if pointer}
	{#if hasExtendedEventLimit}
		{#if device && allDevices && allDevices.filter( (i) => i.id?.startsWith(real_id(device.id)), ).length > 1}
			<Alert
				class="mt-m"
				heading={$format('event_log_constraints_per_device', {
					default: 'The event log can contain more results for this device channel.',
				})}
			/>
		{/if}
		<div class="text-center">
			<Button
				size="m"
				class="mb-m width-full width-md-l"
				on:click={() => getEventLogStatistic(true)}
				loading={waitForResponse}
			>
				{$format('check_for_more_btn', { default: 'Check for more' })}
			</Button>
		</div>
	{/if}
{/if}
